import { useState } from 'react';
import { apiClient } from '../../../api/client';
import { TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_CARD, BG_SURFACE, FONT_HEADING, PRIMARY } from './designTokens';

interface Zone {
  id: string;
  name: string;
  colorCode: string;
  capacity: number | null;
  sortOrder: number;
  setupInstructions?: string | null;
  _count: { tables: number };
}

interface TableInfo {
  id: string;
  tableNumber: number;
  tableName: string;
  zoneId: string;
  capacity: number;
  seats?: { status: string }[];
}

interface ZoneManagerProps {
  zones: Zone[];
  tables?: TableInfo[];
  onZoneCreated: (zone: Zone) => void;
  onZoneUpdated: (zone: Zone) => void;
  onZoneDeleted: (zoneId: string) => void;
}

const styles: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  headerActions: { display: 'flex', gap: '0.5rem' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem' },
  card: {
    backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px',
    padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem',
    cursor: 'pointer', transition: 'box-shadow 0.15s, border-color 0.15s', position: 'relative',
  },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  colorSwatch: { width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0 },
  zoneName: { fontFamily: FONT_HEADING, fontWeight: 700, fontSize: '0.95rem', color: TEXT_DARK, margin: 0, flex: 1 },
  detail: { fontSize: '0.82rem', color: TEXT_MUTED, margin: 0 },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem', backgroundColor: BG_SURFACE, borderRadius: '8px', border: `1px dashed ${BORDER_LIGHT}` },
  emptyText: { fontSize: '0.9rem', color: TEXT_MUTED, margin: 0 },
  addButton: { backgroundColor: PRIMARY, color: '#ffffff', border: 'none', borderRadius: '6px', padding: '0.4rem 0.9rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' },
  printButton: { backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '6px', padding: '0.4rem 0.9rem', fontSize: '0.82rem', fontWeight: 500, cursor: 'pointer' },
  actionButtons: { display: 'flex', gap: '0.25rem', marginLeft: 'auto' },
  iconButton: { background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: '0.85rem', borderRadius: '4px', lineHeight: 1 },
  // Modal styles
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: '#fff', borderRadius: '12px', width: '520px', maxWidth: '92vw', maxHeight: '85vh', overflowY: 'auto', padding: '1.5rem', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  modalTitle: { fontSize: '1.1rem', fontWeight: 700, color: TEXT_DARK, margin: '0 0 1.25rem 0', fontFamily: FONT_HEADING },
  field: { marginBottom: '1rem' },
  label: { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.3rem' },
  input: { width: '100%', padding: '0.5rem 0.7rem', fontSize: '0.85rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '6px', outline: 'none', boxSizing: 'border-box' as const },
  textarea: { width: '100%', padding: '0.5rem 0.7rem', fontSize: '0.85rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '6px', outline: 'none', boxSizing: 'border-box' as const, minHeight: '120px', resize: 'vertical' as const, fontFamily: 'inherit', lineHeight: '1.5' },
  colorRow: { display: 'flex', gap: '0.5rem', alignItems: 'center' },
  modalActions: { display: 'flex', gap: '0.5rem', marginTop: '1.25rem', justifyContent: 'flex-end' },
  btnPrimary: { backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '6px', padding: '0.5rem 1.2rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { backgroundColor: 'transparent', color: TEXT_MUTED, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '6px', padding: '0.5rem 1.2rem', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer' },
  btnDanger: { backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.5rem 1.2rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  apiError: { fontSize: '0.8rem', color: '#c62828', margin: '0.5rem 0 0 0', padding: '0.4rem 0.6rem', backgroundColor: '#ffebee', borderRadius: '4px' },
  // Tooltip
  tooltip: { position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '6px', backgroundColor: '#1f2937', color: '#fff', padding: '8px 12px', borderRadius: '6px', fontSize: '0.75rem', lineHeight: '1.4', whiteSpace: 'pre-wrap', maxWidth: '280px', zIndex: 10, pointerEvents: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' },
};

const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;

export function ZoneManager({ zones, tables = [], onZoneCreated, onZoneUpdated, onZoneDeleted }: ZoneManagerProps) {
  // --- Modal state ---
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editZone, setEditZone] = useState<Zone | null>(null);

  // --- Form state (shared between add/edit modals) ---
  const [formName, setFormName] = useState('');
  const [formColorCode, setFormColorCode] = useState('#1a6b3c');
  const [formCapacity, setFormCapacity] = useState('');
  const [formSetupInstructions, setFormSetupInstructions] = useState('');
  const [formEntryGuidance, setFormEntryGuidance] = useState('');
  const [formError, setFormError] = useState('');
  const [formSaving, setFormSaving] = useState(false);

  // --- Tooltip state ---
  const [hoveredZoneId, setHoveredZoneId] = useState<string | null>(null);

  // --- Setup Report state ---
  const [showReport, setShowReport] = useState(false);
  const [expandedReportZones, setExpandedReportZones] = useState<Set<string>>(new Set());
  const [printIncludeTables, setPrintIncludeTables] = useState(true);

  // --- Open Add Modal ---
  const openAddModal = () => {
    setFormName('');
    setFormColorCode('#1a6b3c');
    setFormCapacity('');
    setFormSetupInstructions('');
    setFormEntryGuidance('');
    setFormError('');
    setShowAddModal(true);
  };

  // --- Open Edit Modal ---
  const openEditModal = (zone: Zone) => {
    setEditZone(zone);
    setFormName(zone.name);
    setFormColorCode(zone.colorCode);
    setFormCapacity(zone.capacity !== null ? String(zone.capacity) : '');
    setFormSetupInstructions(zone.setupInstructions || '');
    setFormEntryGuidance((zone as any).entryGuidance || '');
    setFormError('');
    setShowEditModal(true);
  };

  // --- Validation ---
  function validate(): string | null {
    if (!formName.trim()) return 'Name is required';
    if (formName.trim().length > 50) return 'Name must be 50 characters or less';
    if (!HEX_COLOR_REGEX.test(formColorCode.trim())) return 'Color must be a valid hex (e.g. #1a6b3c)';
    if (formCapacity.trim() !== '') {
      const n = Number(formCapacity.trim());
      if (!Number.isInteger(n) || n < 1) return 'Capacity must be a positive integer';
    }
    return null;
  }

  // --- Submit Add ---
  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) { setFormError(err); return; }
    setFormError('');
    setFormSaving(true);
    try {
      const body: any = { name: formName.trim(), colorCode: formColorCode.trim() };
      if (formCapacity.trim()) body.capacity = parseInt(formCapacity.trim(), 10);
      if (formSetupInstructions.trim()) body.setupInstructions = formSetupInstructions.trim();
      if (formEntryGuidance.trim()) body.entryGuidance = formEntryGuidance.trim();
      const newZone = await apiClient<Zone>('/table-management/zones', { method: 'POST', body });
      onZoneCreated(newZone);
      setShowAddModal(false);
    } catch (err: any) {
      setFormError(err?.message || 'Failed to create zone');
    } finally { setFormSaving(false); }
  };

  // --- Submit Edit ---
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editZone) return;
    const err = validate();
    if (err) { setFormError(err); return; }
    setFormError('');
    setFormSaving(true);
    try {
      const body: any = {};
      if (formName.trim() !== editZone.name) body.name = formName.trim();
      if (formColorCode.trim() !== editZone.colorCode) body.colorCode = formColorCode.trim();
      const newCap = formCapacity.trim() === '' ? null : parseInt(formCapacity.trim(), 10);
      if (newCap !== editZone.capacity) body.capacity = newCap;
      const newInstructions = formSetupInstructions.trim() || null;
      if (newInstructions !== (editZone.setupInstructions || null)) body.setupInstructions = newInstructions || '';
      const newEntryGuidance = formEntryGuidance.trim() || null;
      if (newEntryGuidance !== ((editZone as any).entryGuidance || null)) body.entryGuidance = newEntryGuidance || '';
      if (Object.keys(body).length === 0) { setShowEditModal(false); return; }
      const updatedZone = await apiClient<Zone>(`/table-management/zones/${editZone.id}`, { method: 'PATCH', body });
      onZoneUpdated(updatedZone);
      setShowEditModal(false);
    } catch (err: any) {
      setFormError(err?.message || 'Failed to update zone');
    } finally { setFormSaving(false); }
  };

  // --- Delete ---
  const handleDelete = async () => {
    if (!editZone) return;
    if (!window.confirm(`Are you sure you want to delete zone "${editZone.name}"?`)) return;
    try {
      await apiClient(`/table-management/zones/${editZone.id}`, { method: 'DELETE' });
      onZoneDeleted(editZone.id);
      setShowEditModal(false);
    } catch (err: any) {
      setFormError(err?.message || 'Failed to delete zone');
    }
  };

  // --- Print Setup Report ---
  const toggleReportZone = (zoneId: string) => {
    setExpandedReportZones((prev) => {
      const next = new Set(prev);
      if (next.has(zoneId)) next.delete(zoneId);
      else next.add(zoneId);
      return next;
    });
  };

  const expandAllReportZones = () => {
    setExpandedReportZones(new Set(zones.map((z) => z.id)));
  };

  const collapseAllReportZones = () => {
    setExpandedReportZones(new Set());
  };

  const handlePrintZoneLayout = () => {
    const zoneCards = zones.map((z) => {
      const zoneTables = tables.filter((t) => t.zoneId === z.id);
      const totalSeats = zoneTables.reduce((sum, t) => sum + t.capacity, 0);
      const availableSeats = zoneTables.reduce((sum, t) => {
        if (t.seats && t.seats.length > 0) return sum + t.seats.filter((s) => s.status === 'Available').length;
        return sum + t.capacity;
      }, 0);
      const entryGuide = (z as any).entryGuidance || '';
      return `
        <div class="zone-card" style="border-left:4px solid ${z.colorCode};">
          <div class="zone-header">
            <span class="color-dot" style="background:${z.colorCode};"></span>
            <span class="zone-name">${z.name}</span>
            <span class="zone-cap">Cap/Tbl: ${z.capacity ?? '—'}</span>
          </div>
          ${entryGuide ? `<div class="entry-guide">🚪 ${entryGuide}</div>` : ''}
          ${z.setupInstructions ? `<div class="instructions">${z.setupInstructions.replace(/\n/g, '<br/>')}</div>` : ''}
          <div class="stats">
            <div class="stat green"><div class="stat-val">${zoneTables.length}</div><div class="stat-lbl">Tables</div></div>
            <div class="stat blue"><div class="stat-val">${totalSeats}</div><div class="stat-lbl">Seats</div></div>
            <div class="stat amber"><div class="stat-val">${availableSeats}</div><div class="stat-lbl">Available</div></div>
          </div>
        </div>`;
    }).join('');

    const grandTotalTables = tables.length;
    const grandTotalSeats = tables.reduce((sum, t) => sum + t.capacity, 0);
    const grandTotalAvailable = tables.reduce((sum, t) => {
      if (t.seats && t.seats.length > 0) return sum + t.seats.filter((s) => s.status === 'Available').length;
      return sum + t.capacity;
    }, 0);

    const html = `<!DOCTYPE html><html><head>
      <title>Zone Layout Report — Legacy Ball</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 24px 32px; color: #1f2937; line-height: 1.5; }
        h1 { font-size: 20px; font-weight: 700; margin-bottom: 4px; color: #0f2d1f; }
        p.sub { color: #6b7280; font-size: 12px; margin-bottom: 20px; }
        .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 16px; margin-bottom: 24px; }
        .zone-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px 16px; page-break-inside: avoid; }
        .zone-header { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
        .color-dot { width: 14px; height: 14px; border-radius: 4px; flex-shrink: 0; }
        .zone-name { font-weight: 700; font-size: 14px; color: #0f2d1f; flex: 1; }
        .zone-cap { font-size: 11px; color: #6b7280; }
        .entry-guide { font-size: 11px; color: #92400e; font-weight: 600; background: #fefce8; padding: 5px 10px; border-radius: 4px; margin-bottom: 8px; }
        .instructions { font-size: 11px; color: #374151; white-space: pre-wrap; background: #f9fafb; padding: 8px 10px; border-radius: 4px; margin-bottom: 10px; line-height: 1.5; }
        .stats { display: flex; gap: 8px; }
        .stat { flex: 1; text-align: center; border-radius: 6px; padding: 6px 4px; }
        .stat.green { background: #f0fdf4; }
        .stat.blue { background: #eff6ff; }
        .stat.amber { background: #fefce8; }
        .stat-val { font-size: 16px; font-weight: 700; }
        .stat.green .stat-val { color: #166534; }
        .stat.blue .stat-val { color: #1e40af; }
        .stat.amber .stat-val { color: #854d0e; }
        .stat-lbl { font-size: 9px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.3px; }
        .grand-total { display: flex; gap: 24px; padding: 14px 20px; background: #f3f4f6; border: 2px solid #374151; border-radius: 8px; font-size: 14px; font-weight: 700; color: #0f2d1f; }
        .grand-total .gt-item { display: flex; align-items: baseline; gap: 4px; }
        .grand-total .gt-val { font-size: 18px; }
        .grand-total .gt-lbl { font-size: 11px; color: #6b7280; font-weight: 500; }
        @media print { body { padding: 12px 16px; } .grid { grid-template-columns: repeat(3, 1fr); } }
      </style>
    </head><body>
      <h1>Zone Layout Report — Legacy Ball</h1>
      <p class="sub">${zones.length} zones &bull; ${grandTotalTables} tables &bull; ${grandTotalSeats} seats &bull; Printed ${new Date().toLocaleDateString()}</p>
      <div class="grid">${zoneCards}</div>
      <div class="grand-total">
        <div class="gt-item"><span class="gt-val">${zones.length}</span><span class="gt-lbl">Zones</span></div>
        <div class="gt-item"><span class="gt-val">${grandTotalTables}</span><span class="gt-lbl">Tables</span></div>
        <div class="gt-item"><span class="gt-val">${grandTotalSeats}</span><span class="gt-lbl">Total Seats</span></div>
        <div class="gt-item"><span class="gt-val">${grandTotalAvailable}</span><span class="gt-lbl">Available</span></div>
      </div>
    </body></html>`;
    const printWindow = window.open('', '_blank');
    if (printWindow) { printWindow.document.write(html); printWindow.document.close(); printWindow.focus(); setTimeout(() => printWindow.print(), 300); }
  };

  const handlePrintReport = () => {
    if (!printIncludeTables) {
      // Summary table format — compact single table with all zones
      const rows = zones.map((z) => {
        const zoneTables = tables.filter((t) => t.zoneId === z.id);
        const totalSeats = zoneTables.reduce((sum, t) => sum + t.capacity, 0);
        const availableSeats = zoneTables.reduce((sum, t) => {
          if (t.seats && t.seats.length > 0) return sum + t.seats.filter((s) => s.status === 'Available').length;
          return sum + t.capacity;
        }, 0);
        const instructions = (z.setupInstructions || '—').replace(/\n/g, '<br/>');
        const entryGuide = (z as any).entryGuidance || '';
        return `<tr>
          <td style="white-space:nowrap;"><span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${z.colorCode};margin-right:6px;vertical-align:middle;"></span>${z.name}${entryGuide ? `<div style="font-weight:700;font-size:11px;margin-top:3px;color:#92400e;">🚪 ${entryGuide}</div>` : ''}</td>
          <td style="text-align:center;">${zoneTables.length}</td>
          <td style="text-align:center;">${totalSeats}</td>
          <td style="text-align:center;">${availableSeats}</td>
          <td style="text-align:center;">${z.capacity ?? '—'}</td>
          <td style="white-space:pre-wrap;font-size:11px;line-height:1.4;">${instructions}</td>
        </tr>`;
      }).join('');

      const html = `<!DOCTYPE html><html><head>
        <title>Zone Setup Summary Report</title>
        <style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;padding:24px 32px;color:#1f2937;line-height:1.5;font-size:13px;}h1{font-size:20px;font-weight:700;margin-bottom:4px;color:#0f2d1f;}p.sub{color:#6b7280;font-size:12px;margin-bottom:16px;}table{width:100%;border-collapse:collapse;border:1px solid #e5e7eb;}th{text-align:left;padding:6px 10px;border:1px solid #e5e7eb;font-size:10px;font-weight:600;background:#f3f4f6;color:#374151;text-transform:uppercase;letter-spacing:0.3px;white-space:nowrap;}td{padding:5px 10px;border:1px solid #e5e7eb;font-size:12px;vertical-align:top;}tr:nth-child(even){background:#f9fafb;}th:last-child,td:last-child{width:45%;}@media print{body{padding:12px 16px;}}</style>
      </head><body>
        <h1>Zone Setup Summary Report</h1>
        <p class="sub">${zones.length} zones, ${tables.length} tables &bull; Printed ${new Date().toLocaleDateString()}</p>
        <table>
          <thead><tr><th>Zone Name</th><th style="text-align:center;">Tables</th><th style="text-align:center;">Seats</th><th style="text-align:center;">Available</th><th style="text-align:center;">CAP/TBL</th><th>Setup Instructions</th></tr></thead>
          <tbody>${rows}</tbody>
          <tfoot><tr style="background:#f3f4f6;font-weight:700;border-top:2px solid #374151;">
            <td>GRAND TOTAL</td>
            <td style="text-align:center;">${tables.length}</td>
            <td style="text-align:center;">${zones.reduce((sum, z) => sum + tables.filter((t) => t.zoneId === z.id).reduce((s, t) => s + t.capacity, 0), 0)}</td>
            <td style="text-align:center;">${zones.reduce((sum, z) => sum + tables.filter((t) => t.zoneId === z.id).reduce((s, t) => { if (t.seats && t.seats.length > 0) return s + t.seats.filter((seat) => seat.status === 'Available').length; return s + t.capacity; }, 0), 0)}</td>
            <td style="text-align:center;">—</td>
            <td></td>
          </tr></tfoot>
        </table>
      </body></html>`;
      const printWindow = window.open('', '_blank');
      if (printWindow) { printWindow.document.write(html); printWindow.document.close(); printWindow.focus(); setTimeout(() => printWindow.print(), 300); }
      return;
    }

    // Detailed report with per-zone table listings
    const zoneSections = zones.map((z) => {
      const zoneTables = tables.filter((t) => t.zoneId === z.id).sort((a, b) => a.tableNumber - b.tableNumber);
      const totalSeats = zoneTables.reduce((sum, t) => sum + t.capacity, 0);
      const availableSeats = zoneTables.reduce((sum, t) => {
        if (t.seats && t.seats.length > 0) return sum + t.seats.filter((s) => s.status === 'Available').length;
        return sum + t.capacity;
      }, 0);
      const tableRows = zoneTables.map((t) => {
        const tblAvail = t.seats && t.seats.length > 0 ? t.seats.filter((s) => s.status === 'Available').length : t.capacity;
        return `<tr><td style="font-weight:500;">${t.tableNumber}</td><td>${t.tableName}</td><td style="text-align:center;">${t.capacity}</td><td style="text-align:center;">${tblAvail}</td></tr>`;
      }).join('');
      const instructions = (z.setupInstructions || '').replace(/\n/g, '<br/>');
      const entryGuide = (z as any).entryGuidance || '';
      return `
        <div style="margin-bottom:28px;page-break-inside:avoid;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
            <span style="display:inline-block;width:16px;height:16px;border-radius:4px;background:${z.colorCode};"></span>
            <strong style="font-size:15px;color:#0f2d1f;">${z.name}</strong>
            <span style="font-size:12px;color:#6b7280;margin-left:8px;">${zoneTables.length} tables &bull; ${totalSeats} seats &bull; ${availableSeats} available &bull; cap/tbl: ${z.capacity ?? '—'}</span>
          </div>
          ${instructions ? `<div style="margin:0 0 12px 24px;font-size:12px;color:#374151;white-space:pre-wrap;background:#f9fafb;padding:10px 14px;border-radius:6px;border-left:3px solid ${z.colorCode};line-height:1.6;">${instructions}</div>` : ''}
          ${entryGuide ? `<div style="margin:0 0 12px 24px;font-size:12px;color:#92400e;font-weight:600;background:#fefce8;padding:8px 14px;border-radius:6px;border-left:3px solid #f59e0b;line-height:1.4;">\u{1F6AA} Guest Entry: ${entryGuide}</div>` : ''}
          ${zoneTables.length > 0 ? `<table style="margin-left:24px;">
            <thead><tr><th>Table #</th><th>Table Name</th><th style="text-align:center;">Seats</th><th style="text-align:center;">Available</th></tr></thead>
            <tbody>${tableRows}</tbody>
            <tfoot><tr><td colspan="2">Total: ${zoneTables.length} tables</td><td style="text-align:center;">${totalSeats}</td><td style="text-align:center;">${availableSeats}</td></tr></tfoot>
          </table>` : '<p style="margin-left:24px;font-size:12px;color:#9ca3af;">No tables assigned</p>'}
        </div>`;
    }).join('');

    const grandTotalSeats = zones.reduce((sum, z) => sum + tables.filter((t) => t.zoneId === z.id).reduce((s, t) => s + t.capacity, 0), 0);
    const grandTotalAvailable = zones.reduce((sum, z) => sum + tables.filter((t) => t.zoneId === z.id).reduce((s, t) => { if (t.seats && t.seats.length > 0) return s + t.seats.filter((seat) => seat.status === 'Available').length; return s + t.capacity; }, 0), 0);

    const html = `<!DOCTYPE html><html><head>
      <title>Physical Table/Zone Setup Instructions</title>
      <style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;padding:24px 32px;color:#1f2937;line-height:1.5;font-size:13px;}h1{font-size:20px;font-weight:700;margin-bottom:4px;color:#0f2d1f;}p.sub{color:#6b7280;font-size:12px;margin-bottom:24px;}table{width:100%;border-collapse:collapse;border:1px solid #e5e7eb;border-radius:4px;}th{text-align:left;padding:8px 12px;border:1px solid #e5e7eb;font-size:11px;font-weight:600;background:#f9fafb;color:#374151;text-transform:uppercase;letter-spacing:0.3px;}td{padding:7px 12px;border:1px solid #e5e7eb;font-size:12px;}tfoot td{background:#f9fafb;font-weight:600;border-top:2px solid #d1d5db;}tr:nth-child(even){background:#f9fafb;}.grand-total{margin-top:24px;padding:12px 16px;background:#f3f4f6;border:2px solid #374151;border-radius:6px;font-size:14px;font-weight:700;color:#0f2d1f;}@media print{body{padding:12px 16px;}}</style>
    </head><body>
      <h1>Physical Table/Zone Setup Instructions</h1>
      <p class="sub">${zones.length} zones, ${tables.length} tables &bull; Printed ${new Date().toLocaleDateString()}</p>
      ${zoneSections}
      <div class="grand-total">
        GRAND TOTAL: ${tables.length} tables &bull; ${grandTotalSeats} seats &bull; ${grandTotalAvailable} available
      </div>
    </body></html>`;
    const printWindow = window.open('', '_blank');
    if (printWindow) { printWindow.document.write(html); printWindow.document.close(); printWindow.focus(); setTimeout(() => printWindow.print(), 300); }
  };

  // --- Render Modal Form (shared fields for add/edit) ---
  const renderFormFields = () => (
    <>
      <div style={styles.field}>
        <label style={styles.label}>Name *</label>
        <input style={styles.input} type="text" value={formName} onChange={(e) => setFormName(e.target.value)} maxLength={50} placeholder="e.g. VIP Section" />
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Color Code *</label>
        <div style={styles.colorRow}>
          <input type="color" value={HEX_COLOR_REGEX.test(formColorCode) ? formColorCode : '#1a6b3c'} onChange={(e) => setFormColorCode(e.target.value)} style={{ width: '40px', height: '34px', padding: 0, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', cursor: 'pointer' }} aria-label="Pick zone color" />
          <input style={{ ...styles.input, flex: 1 }} type="text" value={formColorCode} onChange={(e) => setFormColorCode(e.target.value)} placeholder="#1a6b3c" />
        </div>
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Capacity</label>
        <input style={styles.input} type="number" value={formCapacity} onChange={(e) => setFormCapacity(e.target.value)} placeholder="Optional" min={1} />
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Setup Instructions</label>
        <textarea
          style={styles.textarea}
          value={formSetupInstructions}
          onChange={(e) => setFormSetupInstructions(e.target.value)}
          placeholder="Describe how this zone should be physically arranged at the facility&#10;(e.g. table layout, spacing, decorations, signage, power access...)"
        />
        <div style={{ fontSize: '0.72rem', color: TEXT_MUTED, marginTop: '4px' }}>Multiline text. Describe physical layout instructions for the provisioning team.</div>
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Guest Entry Guidance</label>
        <input
          style={styles.input}
          type="text"
          value={formEntryGuidance}
          onChange={(e) => setFormEntryGuidance(e.target.value)}
          maxLength={200}
          placeholder="e.g. Enter through the left doors, your zone is near the stage"
        />
        <div style={{ fontSize: '0.72rem', color: TEXT_MUTED, marginTop: '4px' }}>Shown to guests on their My Weekend page. Directions to find their zone.</div>
      </div>
      {formError && <p style={styles.apiError} role="alert">{formError}</p>}
    </>
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerActions}>
          <button style={styles.addButton} onClick={openAddModal} type="button">+ Add Zone</button>
          {zones.length > 0 && (
            <button style={styles.printButton} onClick={() => setShowReport(!showReport)} type="button">
              {showReport ? '✕ Close Report' : '📋 Setup Report'}
            </button>
          )}
          {zones.length > 0 && (
            <button style={styles.printButton} onClick={handlePrintZoneLayout} type="button">
              🖨️ Print Layout
            </button>
          )}
        </div>
      </div>

      {/* --- Collapsible Setup Report --- */}
      {showReport && zones.length > 0 && (
        <div style={{ backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', padding: '1rem', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: TEXT_DARK, fontFamily: FONT_HEADING }}>Physical Table/Zone Setup Instructions</h4>
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <button style={{ ...styles.printButton, fontSize: '0.75rem', padding: '0.3rem 0.6rem' }} onClick={expandAllReportZones} type="button">Expand All</button>
              <button style={{ ...styles.printButton, fontSize: '0.75rem', padding: '0.3rem 0.6rem' }} onClick={collapseAllReportZones} type="button">Collapse All</button>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', color: TEXT_MUTED, cursor: 'pointer', marginLeft: '0.5rem' }}>
                <input type="checkbox" checked={printIncludeTables} onChange={(e) => setPrintIncludeTables(e.target.checked)} />
                Include table detail
              </label>
              <button style={{ ...styles.addButton, fontSize: '0.75rem', padding: '0.3rem 0.6rem' }} onClick={handlePrintReport} type="button">🖨️ Print</button>
            </div>
          </div>
          <div style={{ fontSize: '0.78rem', color: TEXT_MUTED, marginBottom: '0.75rem' }}>{zones.length} zones, {tables.length} tables</div>
          {zones.map((zone) => {
            const zoneTables = tables.filter((t) => t.zoneId === zone.id).sort((a, b) => a.tableNumber - b.tableNumber);
            const isExpanded = expandedReportZones.has(zone.id);
            return (
              <div key={zone.id} style={{ marginBottom: '0.5rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '6px', overflow: 'hidden' }}>
                <button
                  onClick={() => toggleReportZone(zone.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.6rem 0.75rem', border: 'none', backgroundColor: isExpanded ? '#f0fdf4' : BG_SURFACE, cursor: 'pointer', textAlign: 'left' }}
                  aria-expanded={isExpanded}
                  aria-label={`${zone.name} zone details`}
                >
                  <span style={{ fontSize: '0.7rem', transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s', display: 'inline-block' }}>▼</span>
                  <span style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: zone.colorCode, flexShrink: 0 }} />
                  <span style={{ fontWeight: 600, fontSize: '0.85rem', color: TEXT_DARK, flex: 1 }}>{zone.name}</span>
                  <span style={{ fontSize: '0.75rem', color: TEXT_MUTED }}>{zoneTables.length} tables • cap: {zone.capacity ?? '—'}</span>
                </button>
                {isExpanded && (
                  <div style={{ padding: '0.5rem 0.75rem 0.75rem', borderTop: `1px solid ${BORDER_LIGHT}` }}>
                    {zone.setupInstructions && (
                      <div style={{ fontSize: '0.8rem', color: '#333', whiteSpace: 'pre-wrap', backgroundColor: '#f9fafb', padding: '0.5rem 0.75rem', borderRadius: '4px', borderLeft: `3px solid ${zone.colorCode}`, marginBottom: '0.75rem', lineHeight: '1.5' }}>
                        {zone.setupInstructions}
                      </div>
                    )}
                    {zoneTables.length > 0 ? (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f3f4f6' }}>
                            <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: `2px solid #d1d5db`, fontSize: '0.75rem', fontWeight: 700, color: TEXT_DARK, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Table #</th>
                            <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: `2px solid #d1d5db`, fontSize: '0.75rem', fontWeight: 700, color: TEXT_DARK, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Table Name</th>
                            <th style={{ textAlign: 'center', padding: '8px 12px', borderBottom: `2px solid #d1d5db`, fontSize: '0.75rem', fontWeight: 700, color: TEXT_DARK, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Seats</th>
                          </tr>
                        </thead>
                        <tbody>
                          {zoneTables.map((t, idx) => (
                            <tr key={t.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
                              <td style={{ padding: '6px 12px', borderBottom: `1px solid ${BORDER_LIGHT}`, fontWeight: 600 }}>{t.tableNumber}</td>
                              <td style={{ padding: '6px 12px', borderBottom: `1px solid ${BORDER_LIGHT}` }}>{t.tableName}</td>
                              <td style={{ padding: '6px 12px', borderBottom: `1px solid ${BORDER_LIGHT}`, textAlign: 'center' }}>{t.capacity}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ backgroundColor: '#f3f4f6', fontWeight: 600, fontSize: '0.78rem' }}>
                            <td style={{ padding: '6px 12px', borderTop: `2px solid #d1d5db` }} colSpan={2}>Total: {zoneTables.length} tables</td>
                            <td style={{ padding: '6px 12px', borderTop: `2px solid #d1d5db`, textAlign: 'center' }}>{zoneTables.reduce((sum, t) => sum + t.capacity, 0)} seats</td>
                          </tr>
                        </tfoot>
                      </table>
                    ) : (
                      <p style={{ fontSize: '0.8rem', color: TEXT_MUTED, margin: 0 }}>No tables assigned to this zone</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {zones.length === 0 ? (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>No zones configured yet</p>
        </div>
      ) : (
        <div style={styles.grid}>
          {zones.map((zone) => {
            const hasInstructions = !!zone.setupInstructions?.trim();
            return (
              <div
                key={zone.id}
                style={styles.card}
                onClick={() => openEditModal(zone)}
                onMouseEnter={() => hasInstructions && setHoveredZoneId(zone.id)}
                onMouseLeave={() => setHoveredZoneId(null)}
                title={hasInstructions ? undefined : 'Click to edit zone'}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') openEditModal(zone); }}
                aria-label={`Edit zone ${zone.name}`}
              >
                {/* Tooltip */}
                {hoveredZoneId === zone.id && hasInstructions && (
                  <div style={styles.tooltip}>{zone.setupInstructions}</div>
                )}
                <div style={styles.cardHeader}>
                  <span style={{ ...styles.colorSwatch, backgroundColor: zone.colorCode }} aria-label={`Zone color: ${zone.colorCode}`} />
                  <h4 style={styles.zoneName}>{zone.name}</h4>
                  <div style={styles.actionButtons}>
                    <button
                      style={{ ...styles.iconButton, opacity: hasInstructions ? 1 : 0.3 }}
                      onClick={(e) => { e.stopPropagation(); if (hasInstructions) setHoveredZoneId(hoveredZoneId === zone.id ? null : zone.id); }}
                      disabled={!hasInstructions}
                      title={hasInstructions ? 'View setup instructions' : 'No setup instructions'}
                      aria-label={hasInstructions ? `View instructions for ${zone.name}` : 'No setup instructions'}
                    >
                      📋
                    </button>
                    <span
                      style={{ ...styles.iconButton, opacity: (zone as any).entryGuidance ? 1 : 0.3, cursor: 'default' }}
                      title={(zone as any).entryGuidance || 'No entry guidance set'}
                      aria-label={(zone as any).entryGuidance ? `Entry: ${(zone as any).entryGuidance}` : 'No entry guidance'}
                    >
                      🚪
                    </span>
                  </div>
                </div>
                <p style={{ ...styles.detail, fontSize: '0.7rem', textAlign: 'left' }}>Capacity/Table: {zone.capacity !== null ? zone.capacity : '—'}</p>
                {/* Stat pills */}
                {(() => {
                  const zoneTables = tables.filter((t) => t.zoneId === zone.id);
                  const seatCount = zoneTables.reduce((sum, t) => sum + t.capacity, 0);
                  const availableSeats = zoneTables.reduce((sum, t) => {
                    if (t.seats && t.seats.length > 0) {
                      return sum + t.seats.filter((s) => s.status === 'Available').length;
                    }
                    return sum + t.capacity; // If no seat data, assume all available
                  }, 0);
                  return (
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px', paddingTop: '6px', borderTop: `1px solid ${BORDER_LIGHT}` }}>
                      <div style={{ flex: 1, textAlign: 'center', backgroundColor: '#f0fdf4', borderRadius: '6px', padding: '4px 6px' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#166534' }}>{zone._count.tables}</div>
                        <div style={{ fontSize: '0.62rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Tables</div>
                      </div>
                      <div style={{ flex: 1, textAlign: 'center', backgroundColor: '#eff6ff', borderRadius: '6px', padding: '4px 6px' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e40af' }}>{seatCount}</div>
                        <div style={{ fontSize: '0.62rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Seats</div>
                      </div>
                      <div style={{ flex: 1, textAlign: 'center', backgroundColor: '#fefce8', borderRadius: '6px', padding: '4px 6px' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#854d0e' }}>{availableSeats}</div>
                        <div style={{ fontSize: '0.62rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Available</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      {/* --- Add Zone Modal --- */}
      {showAddModal && (
        <div style={styles.overlay} onClick={() => !formSaving && setShowAddModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Add new zone">
            <h3 style={styles.modalTitle}>Add Zone</h3>
            <form onSubmit={handleAddSubmit} noValidate>
              {renderFormFields()}
              <div style={styles.modalActions}>
                <button type="button" style={styles.btnSecondary} onClick={() => setShowAddModal(false)} disabled={formSaving}>Cancel</button>
                <button type="submit" style={styles.btnPrimary} disabled={formSaving}>{formSaving ? 'Creating…' : 'Create Zone'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- Edit Zone Modal --- */}
      {showEditModal && editZone && (
        <div style={styles.overlay} onClick={() => !formSaving && setShowEditModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Edit zone ${editZone.name}`}>
            <h3 style={styles.modalTitle}>Edit Zone — {editZone.name}</h3>
            <form onSubmit={handleEditSubmit} noValidate>
              {renderFormFields()}
              <div style={{ ...styles.modalActions, justifyContent: 'space-between' }}>
                <button
                  type="button"
                  style={{ ...styles.btnDanger, opacity: editZone._count.tables > 0 ? 0.5 : 1 }}
                  onClick={handleDelete}
                  disabled={editZone._count.tables > 0 || formSaving}
                  title={editZone._count.tables > 0 ? 'Reassign tables before deleting' : 'Delete this zone'}
                >
                  Delete Zone
                </button>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="button" style={styles.btnSecondary} onClick={() => setShowEditModal(false)} disabled={formSaving}>Cancel</button>
                  <button type="submit" style={styles.btnPrimary} disabled={formSaving}>{formSaving ? 'Saving…' : 'Save Changes'}</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
